import {Box} from '@mui/material';
import {t} from 'i18next';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {Dispatch, SetStateAction, useCallback, useState} from 'react';
import AurPopup from '../../common/aur-popup/AurPopup';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {MAX_CHAR_LIMIT} from '../../../constants/constants';
import AurBanner from '../../common/aur-banner/AurBanner';
import {AurLoadingButton} from '../../common/aur-loading-button/AurLoadingButton';
import {useAddNewTag} from '../../../hooks/eca-tagging/useAddNewTag';
import {ITag} from '../../pages/field-management/custom-field/DraggableTag';
import {isValidTagName} from '../../../utils/utils';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

export const NEW_TAG_CONTAINER_HEIGHT = 112;
export const NEW_TAG_ERROR_CONTAINER_HEIGHT = 64;

export interface IAddNewTagBody {
  value: string;
  displayOrder: number;
}

type AddNewTagProps = {
  onAddClick: (tag: ITag) => void;
  allTags: ITag[];
  tagFieldId: string;
  displayOrder: number;
  isError: boolean;
  setError: Dispatch<SetStateAction<boolean>>;
};

const errorIcon = (
  <AurIcon icon={IconNames.Error} color={IconColors.Critical} />
);

const AddNewTag = ({
  onAddClick,
  allTags,
  isError,
  setError,
  tagFieldId,
  displayOrder,
}: AddNewTagProps) => {
  const [newTagName, setNewTagName] = useState('');
  const [openDuplicateTagModal, setopenDuplicateTagModal] = useState(false);
  const {addTag, isLoading} = useAddNewTag();

  const {hasCreateOrEditTagFieldsPerm} = usePermissionsByPage(
    PAGE_NAME.FIELD_AND_TAG_MANAGEMENT
  );
  const {trackGAEvent} = useAnalytics();

  const validateName = useCallback(
    (name: string): boolean => {
      const isValid = isValidTagName(name);
      setError(!isValid);
      return isValid;
    },
    [setError]
  );

  const handleAddTag = useCallback(async () => {
    if (!validateName(newTagName)) {
      return;
    }

    const isDuplicate = allTags.some(
      (tag) => tag.tagValue.toLowerCase() === newTagName.toLowerCase()
    );

    if (isDuplicate) {
      setopenDuplicateTagModal(true);
      return;
    }

    const onTagSuccess = (tagId: string) => {
      onAddClick({
        tagValueId: Number(tagId),
        tagValue: newTagName,
        displayOrder,
      });
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.global.add'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.DRAWER,
          action: t('gaTracking.str.newTagAction'),
        },
        subPage: {
          subpage_name: t('gaTracking.str.bulkTaggingTags'),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    };

    addTag(
      {
        value: newTagName,
        displayOrder,
      },
      tagFieldId,
      onTagSuccess
    );

    setNewTagName('');
  }, [
    validateName,
    addTag,
    allTags,
    displayOrder,
    newTagName,
    onAddClick,
    tagFieldId,
  ]);

  const newTagChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewTagName(e.target.value);
      setError(false);
    },
    []
  );

  const handleOk = useCallback(() => {
    setopenDuplicateTagModal(false);
    setNewTagName('');
  }, []);

  return (
    <Box
      sx={{
        height:
          NEW_TAG_CONTAINER_HEIGHT +
          (isError ? NEW_TAG_ERROR_CONTAINER_HEIGHT : 0),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingX: 2,
        paddingTop: isError ? 2 : 0,
      }}
    >
      {isError && (
        <AurBanner
          errorText={t('ecaDocList.bulkTagging.addNewTag.tagNameError')}
          errorType="error"
          showIcon={true}
        />
      )}
      {hasCreateOrEditTagFieldsPerm && (
        <AurTextField
          error={isError}
          name="add-new-tag"
          onChange={newTagChangeHandler}
          value={newTagName}
          helperText={t('ecaDocList.bulkTagging.addNewTag.helperText')}
          inputProps={{
            maxLength: MAX_CHAR_LIMIT,
          }}
          ariaLabel={t('ecaDocList.bulkTagging.addNewTag.create')}
          InputProps={{
            endAdornment: (
              <AurLoadingButton
                label={t('common.ipAddress.add')}
                loading={isLoading}
                onClick={handleAddTag}
                size="Small"
                buttonType="submit"
                classNames={`typo-text small-border-radius-button ${isLoading && 'apply-button-loading'}`}
                disabled={!newTagName || isError}
                dataTestId="add-new-tag"
              />
            ),
          }}
          label={t('ecaDocList.bulkTagging.addNewTag.create')}
          variant="filled"
          className={`aur-textfield  aur-new-tag-textfield`}
        />
      )}
      {openDuplicateTagModal && (
        <AurPopup
          className="aur-unsaved-changes-popup duplicate-tag-confirmation-popup"
          open={openDuplicateTagModal}
          onOk={handleOk}
          onCancel={handleOk}
          popupHeading={t('ecaDocList.bulkTagging.addNewTag.popupHeading')}
          showPopupHeading={true}
          popupContent={t(
            'ecaDocList.bulkTagging.addNewTag.duplicateTagSubheading'
          )}
          labelActionOk={t('ecaDocList.bulkTagging.addNewTag.okay')}
          headingIcon={errorIcon}
          showCancelOption={false}
        />
      )}
    </Box>
  );
};

export default AddNewTag;
